import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { query, where } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import moment from 'moment-timezone';
import FirebaseAuth from '../services/FirebaseAuth';
import { McpOAuthRefreshTokenModel } from '../models/database/McpOAuthRefreshToken';
import { McpOAuthClientModel } from '../models/database/McpOAuthClient';
import { useExpireMcpClient } from '../methods/expireMcpClient';
import Loader from '../components/Loader';
import './McpClientsRoute.css';

interface SnackMsg {
  type: 'success' | 'error';
  message: string;
  isShown: boolean;
}

interface ClientRow {
  id: string;
  clientName: string;
  redirectUris: string[];
  clientIdIssuedAt?: number;
  clientExpiresAt: number;
  refreshTokensCount: number;
}

function McpClientsRoute() {
  const [user] = useAuthState(FirebaseAuth);
  const refreshTokensQuery = useMemo(
    () =>
      user
        ? query(McpOAuthRefreshTokenModel.parent, where('userId', '==', user.uid))
        : null,
    [user]
  );
  const [refreshTokens, refreshTokensLoading] = useCollectionData(
    refreshTokensQuery
  );

  const expireMcpClient = useExpireMcpClient();
  const [expiringId, setExpiringId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [showMsg, setShowMsg] = useState<SnackMsg>({
    type: 'success',
    message: '',
    isShown: false,
  });
  const [clients, setClients] = useState<Map<string, McpOAuthClientModel>>(
    new Map()
  );
  const [clientsLoading, setClientsLoading] = useState(false);

  const clientIds = useMemo(() => {
    const ids = new Set<string>();
    refreshTokens?.forEach((rt) => {
      if (rt.clientId) ids.add(rt.clientId);
    });
    return Array.from(ids);
  }, [refreshTokens]);

  useEffect(() => {
    let cancelled = false;
    if (clientIds.length === 0) {
      setClients(new Map());
      return;
    }
    setClientsLoading(true);
    Promise.all(
      clientIds.map(async (id) => {
        try {
          const snap = await McpOAuthClientModel.withId(id);
          return snap.exists() ? snap.data() ?? null : null;
        } catch {
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const next = new Map<string, McpOAuthClientModel>();
      results.forEach((c) => {
        if (c) next.set(c.id, c);
      });
      setClients(next);
      setClientsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [clientIds]);

  const rows: ClientRow[] = useMemo(() => {
    if (!refreshTokens) return [];
    const counts = new Map<string, number>();
    refreshTokens.forEach((rt) => {
      counts.set(rt.clientId, (counts.get(rt.clientId) ?? 0) + 1);
    });
    const list: ClientRow[] = [];
    counts.forEach((count, clientId) => {
      const client = clients.get(clientId);
      if (!client) return;
      list.push({
        id: client.id,
        clientName: client.clientData.client_name ?? client.id,
        redirectUris: Array.isArray(client.clientData.redirect_uris)
          ? client.clientData.redirect_uris
          : [],
        clientIdIssuedAt: client.clientData.client_id_issued_at,
        clientExpiresAt: client.expiresAt,
        refreshTokensCount: count,
      });
    });
    list.sort((a, b) => a.clientName.localeCompare(b.clientName));
    return list;
  }, [refreshTokens, clients]);

  const handleExpire = async (clientId: string) => {
    setConfirmId(null);
    setExpiringId(clientId);
    try {
      const res = await expireMcpClient[0]({ id: clientId });
      const revoked = res.data.revoked;
      setShowMsg({
        type: 'success',
        message: `Client expired (${revoked} token${revoked === 1 ? '' : 's'} revoked)`,
        isShown: true,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to expire client';
      setShowMsg({ type: 'error', message: msg, isShown: true });
    } finally {
      setExpiringId(null);
    }
  };

  if (refreshTokensLoading || (clientIds.length > 0 && clientsLoading && clients.size === 0)) {
    return <Loader />;
  }

  return (
    <div className="McpClientsRoute">
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        <AlertTitle>MCP server endpoint</AlertTitle>
        <Typography variant="body2">
          Connect any MCP-compatible client (e.g. Claude Desktop, Cursor) to{' '}
          <Link
            href="https://mcp.miniaccountant.app/v1beta"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://mcp.miniaccountant.app/v1beta
          </Link>
          . The client will register itself via dynamic OAuth and appear in the
          table below once you complete the consent flow.
        </Typography>
      </Alert>
      {rows.length === 0 ? (
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No MCP clients connected yet.
          </Typography>
        </Toolbar>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="mcp clients">
            <TableHead>
              <TableRow>
                <TableCell align="center">Client</TableCell>
                <TableCell align="center">Client ID</TableCell>
                <TableCell align="center">Redirect URIs</TableCell>
                <TableCell align="center">Tokens</TableCell>
                <TableCell align="center">Registered</TableCell>
                <TableCell align="center">Expires</TableCell>
                <TableCell align="center">Expire</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((c) => (
                <TableRow
                  key={`mcp_row_${c.id}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="center">{c.clientName}</TableCell>
                  <TableCell align="center">{c.id}</TableCell>
                  <TableCell align="center">
                    <div className="McpClientsRoute__redirects">
                      {c.redirectUris.length > 0
                        ? c.redirectUris.join(', ')
                        : '—'}
                    </div>
                  </TableCell>
                  <TableCell align="center">{c.refreshTokensCount}</TableCell>
                  <TableCell align="center">
                    {c.clientIdIssuedAt
                      ? moment(c.clientIdIssuedAt * 1000).format('YYYY-MM-DD')
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {moment(c.clientExpiresAt).format('YYYY-MM-DD HH:mm')}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={expiringId === c.id}
                      onClick={() => setConfirmId(c.id)}
                    >
                      {expiringId === c.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <LinkOffOutlinedIcon />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={showMsg.isShown}
        autoHideDuration={6000}
        onClose={() => setShowMsg({ ...showMsg, isShown: false })}
        sx={{ width: '100%', padding: '3vmin', justifyContent: 'end' }}
      >
        <Alert
          onClose={() => setShowMsg({ ...showMsg, isShown: false })}
          sx={{ marginRight: '3vmin' }}
          severity={showMsg.type}
        >
          {showMsg.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmId !== null} onClose={() => setConfirmId(null)}>
        <DialogTitle>Expire MCP client?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revoke all refresh tokens this client holds for your
            account. The client will need to go through the OAuth consent flow
            again to regain access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => confirmId && handleExpire(confirmId)}
          >
            Expire
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default McpClientsRoute;
